import type { LatLng } from '@/state/types';

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance in kilometers between two lat/lng points. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/** Linear interpolation between two lat/lng points. */
export function lerpLatLng(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
}

/** Sample a random point within `radiusKm` of `center` (uniform-ish). */
export function randomPointNear(center: LatLng, radiusKm: number): LatLng {
  const r = radiusKm * Math.sqrt(Math.random());
  const theta = Math.random() * 2 * Math.PI;
  // 1 deg latitude ≈ 111.32 km
  const dLat = (r * Math.cos(theta)) / 111.32;
  const dLng = (r * Math.sin(theta)) / (111.32 * Math.cos(toRad(center.lat)));
  return { lat: center.lat + dLat, lng: center.lng + dLng };
}

/** Total length of a GeoJSON LineString (in km), given [lng,lat] coords. */
export function lineStringLengthKm(coords: [number, number][]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const [lng1, lat1] = coords[i - 1];
    const [lng2, lat2] = coords[i];
    total += haversineKm({ lat: lat1, lng: lng1 }, { lat: lat2, lng: lng2 });
  }
  return total;
}

/**
 * Approximate circle as a GeoJSON LineString/Polygon coordinate ring.
 * Returns [lng, lat] pairs, closed (last equals first).
 */
export function makeCirclePolygon(
  center: LatLng,
  radiusKm: number,
  segments = 64,
): [number, number][] {
  const coords: [number, number][] = [];
  const R = 6371; // km
  const lat0 = (center.lat * Math.PI) / 180;
  const lng0 = (center.lng * Math.PI) / 180;
  const d = radiusKm / R;

  for (let i = 0; i <= segments; i++) {
    const theta = (i / segments) * 2 * Math.PI;
    const lat = Math.asin(
      Math.sin(lat0) * Math.cos(d) + Math.cos(lat0) * Math.sin(d) * Math.cos(theta),
    );
    const lng =
      lng0 +
      Math.atan2(
        Math.sin(theta) * Math.sin(d) * Math.cos(lat0),
        Math.cos(d) - Math.sin(lat0) * Math.sin(lat),
      );
    coords.push([(lng * 180) / Math.PI, (lat * 180) / Math.PI]);
  }
  return coords;
}

/** Position along a line string at fractional distance t (0..1). */
export function pointAlongLine(coords: [number, number][], t: number): LatLng {
  if (coords.length === 0) return { lat: 0, lng: 0 };
  if (coords.length === 1) {
    const [lng, lat] = coords[0];
    return { lat, lng };
  }
  const clampedT = Math.max(0, Math.min(1, t));
  const totalKm = lineStringLengthKm(coords);
  if (totalKm === 0) {
    const [lng, lat] = coords[0];
    return { lat, lng };
  }
  const targetKm = clampedT * totalKm;
  let acc = 0;
  for (let i = 1; i < coords.length; i++) {
    const [lng1, lat1] = coords[i - 1];
    const [lng2, lat2] = coords[i];
    const segKm = haversineKm({ lat: lat1, lng: lng1 }, { lat: lat2, lng: lng2 });
    if (acc + segKm >= targetKm) {
      const segT = segKm === 0 ? 0 : (targetKm - acc) / segKm;
      return lerpLatLng({ lat: lat1, lng: lng1 }, { lat: lat2, lng: lng2 }, segT);
    }
    acc += segKm;
  }
  const [lng, lat] = coords[coords.length - 1];
  return { lat, lng };
}
